import React, { useState, useEffect } from "react";
import { Modal, Button, Carousel } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { BiFullscreen, BiExitFullscreen } from "react-icons/bi";
import { BiDownload } from "react-icons/bi";
import "./ImageModal.css";

// Simple in-memory cache to remember which image URLs have been loaded
const loadedImageCache = new Set();

const isImageCached = (url) => {
  if (!url) return false;
  if (loadedImageCache.has(url)) return true;
  try {
    const probe = new Image();
    probe.src = url;
    if (probe.complete && probe.naturalWidth > 0) {
      loadedImageCache.add(url);
      return true;
    }
  } catch (_) {}
  return false;
};

const preloadAllImages = (urls) => {
  if (!Array.isArray(urls)) return;
  urls.forEach((src) => {
    if (!src || loadedImageCache.has(src)) return;
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = src;
    img.onload = () => loadedImageCache.add(src);
  });
};

const ImageModal = ({ show, onHide, images, hotelName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (show) {
      setCurrentIndex(0);
      // Preload every image as soon as the modal opens
      preloadAllImages(images);
      // If the first image is already cached, skip spinner
      const firstSrc = images && images.length ? images[0] : null;
      setImageLoading(firstSrc ? !isImageCached(firstSrc) : false);
    }
  }, [show]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!show) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePrevious();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNext();
          break;
        case "Escape":
          event.preventDefault();
          onHide();
          break;
        case "Home":
          event.preventDefault();
          setCurrentIndex(0);
          break;
        case "End":
          event.preventDefault();
          setCurrentIndex(images.length - 1);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, images.length]);

  const handlePrevious = () => {
    const nextIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    const nextSrc = images[nextIndex];
    setImageLoading(!isImageCached(nextSrc));
    setCurrentIndex(nextIndex);
  };

  const handleNext = () => {
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    const nextSrc = images[nextIndex];
    setImageLoading(!isImageCached(nextSrc));
    setCurrentIndex(nextIndex);
  };

  const handleThumbnailClick = (index) => {
    const nextSrc = images[index];
    setImageLoading(!isImageCached(nextSrc));
    setCurrentIndex(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const downloadImage = () => {
    const link = document.createElement("a");
    link.href = images[currentIndex];
    link.download = `${hotelName}_image_${currentIndex + 1}.jpg`;
    link.target = "_blank"; // Optional: avoids interfering with current tab
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Touch/swipe support for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (!images || images.length === 0) return null;

  const openImageInNewTab = () => {
    window.open(images[currentIndex], "_blank");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className={`image-modal ${isFullscreen ? "image-modal-fullscreen" : ""}`}
    >
      <Modal.Header className="image-modal-header">
        <Modal.Title className="image-modal-title">{hotelName}</Modal.Title>
        <div className="image-modal-header-buttons">
          <Button
            variant="link"
            // onClick={openImageInNewTab}
            onClick={downloadImage}
            className="image-modal-download-btn"
            title="Download image"
          >
            <BiDownload size={20} />
          </Button>
          <Button
            variant="link"
            onClick={toggleFullscreen}
            className="image-modal-fullscreen-btn"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <BiExitFullscreen size={20} />
            ) : (
              <BiFullscreen size={20} />
            )}
          </Button>
          <Button
            variant="link"
            onClick={onHide}
            className="image-modal-close-btn"
          >
            <IoClose size={24} />
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="image-modal-body">
        <div className="image-modal-main">
          <Button
            variant="link"
            onClick={handlePrevious}
            className="image-modal-nav-btn image-modal-prev-btn"
            aria-label="Previous image"
            title="Previous image (←)"
          >
            <IoIosArrowBack size={24} />
          </Button>

          <div
            className="image-modal-image-container"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {imageLoading && (
              <div className="image-modal-loading">
                <div className="spinner-border text-light" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <img
              src={images[currentIndex]}
              alt={`${hotelName} - Image ${currentIndex + 1}`}
              className="image-modal-main-image"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              style={{ opacity: imageLoading ? 0 : 1 }}
              onLoad={() => {
                loadedImageCache.add(images[currentIndex]);
                setImageLoading(false);
              }}
              onError={() => setImageLoading(false)}
            />
            <div className="image-modal-counter">
              {currentIndex + 1} / {images.length}
            </div>
          </div>

          <Button
            variant="link"
            onClick={handleNext}
            className="image-modal-nav-btn image-modal-next-btn"
            aria-label="Next image"
            title="Next image (→)"
          >
            <IoIosArrowForward size={24} />
          </Button>
        </div>

        {images.length > 1 && (
          <>
            <div className="image-modal-progress">
              <div
                className="image-modal-progress-bar"
                style={{
                  width: `${((currentIndex + 1) / images.length) * 100}%`,
                }}
              ></div>
            </div>
            <div className="image-modal-thumbnails">
              {images.map((image, index) => (
                <div
                  key={index}
                  className={`image-modal-thumbnail ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                  data-index={index + 1}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="image-modal-thumbnail-img"
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ImageModal;
